import { RefreshCcw, AlertTriangle } from 'lucide-react'
import { Component, type ErrorInfo, type ReactNode } from 'react'

import { Button } from '@/components/ui/button'

interface Props {
    children?: ReactNode
    fallback?: ReactNode
}

interface State {
    hasError: boolean
    error: Error | null
}

export class ErrorBoundary extends Component<Props, State> {
    public state: State = {
        hasError: false,
        error: null,
    }

    public static getDerivedStateFromError(error: Error): State {
        return { hasError: true, error }
    }

    public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        console.error('Uncaught error:', error, errorInfo)
    }

    private handleReload = () => {
        window.location.reload()
    }

    public render() {
        if (this.state.hasError) {
            if (this.props.fallback) {
                return this.props.fallback
            }

            return (
                <div className="flex min-h-[400px] w-full flex-col items-center justify-center p-8 text-center bg-background rounded-lg border border-border shadow-sm">
                    <div className="mb-4 rounded-full bg-red-100 p-3 dark:bg-red-900/20">
                        <AlertTriangle className="h-8 w-8 text-red-600 dark:text-red-400" />
                    </div>
                    <h2 className="mb-2 text-xl font-semibold text-foreground">
                        出错了
                    </h2>
                    <p className="mb-6 max-w-md text-sm text-muted-foreground">
                        抱歉，应用程序遇到意外错误。如果是网络问题，请尝试刷新页面。
                    </p>

                    {process.env.NODE_ENV === 'development' && this.state.error && (
                        <div className="mb-6 w-full max-w-lg overflow-auto rounded bg-muted p-4 text-left">
                            <pre className="text-xs text-muted-foreground whitespace-pre-wrap">
                                {this.state.error.toString()}
                            </pre>
                        </div>
                    )}

                    <Button variant="outline" onClick={this.handleReload} className="gap-2">
                        <RefreshCcw className="h-4 w-4" />
                        刷新页面
                    </Button>
                </div>
            )
        }

        return this.props.children
    }
}
